import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Award, Users, Star, Check, Phone, MessageCircle, MapPin, Plane } from "lucide-react";
import { WA_LINK, CALL_LINK } from "@/data";

const inView = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export default function About() {
  const stats = [
    { icon: <Award size={28} className="text-[#FF4500]" />, value: "15+", label: "Years Experience" },
    { icon: <Users size={28} className="text-[#FF4500]" />, value: "1,000+", label: "Happy Clients" },
    { icon: <Star size={28} className="text-[#FF4500] fill-[#FF4500]" />, value: "4.9", label: "Google Rating" },
  ];

  return (
    <>
      <Helmet>
        <title>About Raj S | Certified Male Massage Therapist Mumbai | 15+ Years</title>
        <meta name="description" content="Meet Raj S, certified male massage therapist with 15+ years experience in Mumbai. Specialized in professional massage therapy for men — studio, home & hotel service in Andheri." />
        <meta name="keywords" content="certified massage therapist Mumbai, male massage therapist Andheri, professional massage for men Mumbai, massage therapist 15 years experience" />
        <link rel="canonical" href="https://rajmusclecarestudio.com/about" />
        <meta property="og:title" content="About Raj S | Certified Male Massage Therapist Mumbai" />
        <meta property="og:description" content="15+ years experience. Professional massage therapy for men. Studio, home & hotel service in Andheri, Mumbai." />
        <meta property="og:url" content="https://rajmusclecarestudio.com/about" />
      </Helmet>
      <div className="min-h-screen bg-[#050505]">
      {/* Page Hero */}
      <div className="relative pt-32 pb-16 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF4500]/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-80 h-80 bg-[#FF4500]/6 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF4500] mb-3 font-inter">
              About
            </p>
            <h1 className="text-4xl sm:text-5xl text-white font-black mb-5 leading-tight">
              Meet Raj S
              <br />
              <span className="text-[#FF4500]">Your Therapist</span>
            </h1>
            <p className="text-gray-400 font-inter text-base leading-relaxed max-w-xl mx-auto">
              15+ years of certified massage therapy experience in Mumbai
            </p>
          </motion.div>
        </div>
      </div>

      {/* About Content */}
      <section className="py-10 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Text */}
            <motion.div {...inView} transition={{ duration: 0.7 }}>
              <h2 className="text-2xl sm:text-3xl text-white font-black mb-5 leading-snug">
                Professional Male Massage Therapist in Mumbai
              </h2>
              <p className="text-gray-300 font-inter text-base leading-relaxed mb-5">
                Hello, I'm Raj S, a certified male massage therapist with 15+ years of experience
                in Mumbai. I specialize in professional massage therapy for men, offering stress relief,
                muscle recovery, and overall wellness.
              </p>
              <p className="text-gray-300 font-inter text-base leading-relaxed mb-8">
                I provide male to male massage in Mumbai with services available at your home, hotel
                room, or my private studio in Andheri — just 15 minutes from Mumbai Airport. Whether
                you're a Mumbai resident or a traveller, I'm here to help you unwind and recover.
              </p>

              <div className="flex flex-col gap-4 mb-8">
                {[
                  "Certified & Experienced Therapist",
                  "Professional & Discreet Service",
                  "Trusted by 1,000+ Regular & Premium Clients",
                  "Studio, Home & Hotel Service Available",
                  "15 Minutes from Mumbai Airport",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FF4500]/20 flex items-center justify-center">
                      <Check size={13} className="text-[#FF4500]" />
                    </span>
                    <span className="text-gray-300 text-sm font-inter">{item}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#e63d00] text-white font-semibold px-7 py-4 rounded-full transition-all duration-200 hover:scale-105 min-h-[56px]"
                  data-testid="about-book-btn"
                >
                  <MessageCircle size={18} />
                  Book on WhatsApp
                </a>
                <a
                  href="tel:+919004886964"
                  className="flex items-center justify-center gap-2 border border-white/30 text-white font-semibold px-7 py-4 rounded-full transition-all hover:bg-white/5 min-h-[56px]"
                  data-testid="about-call-btn"
                >
                  <Phone size={18} />
                  Call Now
                </a>
              </div>
            </motion.div>

            {/* Stats + Info */}
            <motion.div {...inView} transition={{ duration: 0.7, delay: 0.15 }} className="flex flex-col gap-5">
              {/* Trust stats */}
              <div className="grid grid-cols-3 gap-4">
                {stats.map((s, i) => (
                  <div
                    key={i}
                    className="bg-[#111111] rounded-2xl p-5 border border-white/10 text-center hover:border-[#FF4500]/30 transition-colors"
                  >
                    <div className="flex justify-center mb-2">{s.icon}</div>
                    <div className="font-playfair text-2xl font-black text-white">{s.value}</div>
                    <div className="text-gray-400 text-xs font-inter mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Studio info card */}
              <div className="bg-[#111111] rounded-2xl p-6 border border-white/10">
                <h3 className="text-white font-bold text-lg mb-4">Studio Location</h3>
                <div className="flex gap-3 mb-3">
                  <MapPin size={16} className="text-[#FF4500] mt-0.5 shrink-0" />
                  <p className="text-gray-400 text-sm font-inter leading-relaxed">
                    Shop No. 1B, Venus Tower, Behind Axis Bank, Veera Desai Road, Andheri West, Mumbai — 400053
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-3 p-3 rounded-xl bg-[#FF4500]/10 border border-[#FF4500]/20">
                  <Plane size={16} className="text-[#FF4500] shrink-0" />
                  <p className="text-[#FF4500] text-sm font-semibold font-inter">
                    Just 15 minutes from Mumbai Airport
                  </p>
                </div>
              </div>

              {/* Working hours */}
              <div className="bg-[#111111] rounded-2xl p-6 border border-white/10">
                <h3 className="text-white font-bold text-lg mb-4">Working Hours</h3>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm font-inter">Studio (All Days)</span>
                    <span className="text-white text-sm font-semibold font-inter">8:00 AM – 11:30 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm font-inter">Hotel Service</span>
                    <span className="text-[#FF4500] text-sm font-semibold font-inter">On Request</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Privacy Promise */}
      <section className="py-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div {...inView} transition={{ duration: 0.6 }}>
            <p className="text-gray-400 font-inter text-sm leading-relaxed">
              <span className="text-[#FF4500] font-semibold">Privacy Promise:</span>{" "}
              100% Discreet &amp; Confidential Massage Service in Mumbai.
              Your privacy is our top priority — always.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
}
